// src/image-rects.ts
// Computes the bounding rects of images painted on a page, for
// RequestType.GET_IMAGE_RECTS (click-on-image redaction marking). Walks the
// pdfjs operator list, replaying the transform state, and maps each image
// paint's unit square into stored-markup space (scale 1 / 96 DPI, default
// rotation — same convention as find-match-rects/page-rects).
//
// OPS is passed in rather than imported statically: pdfjs-dist is only ever
// loaded dynamically in this codebase (see main.ts's `await import(...)`)
// because its module-level code touches DOM globals (e.g. DOMMatrix) that
// don't exist under the Node/vitest test runner. Callers pass `pdfjsLib.OPS`.
import { MatchRect, ImageRectsResponse } from './shared/model/docavea-viewer-interface';

const CSS_UNITS = 96.0 / 72.0;

type Matrix = [number, number, number, number, number, number];
const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

// Returns the matrix equivalent to applying `inner` first, then `outer` —
// i.e. composeMatrix(outer, inner)(p) === outer(inner(p)). This mirrors
// pdfjs's own `Util.transform(m1, m2)` (src/shared/util.js), which canvas.js
// relies on implicitly via ctx.transform()'s pre-multiply semantics when
// replaying the `cm` operator: the newest matrix is applied to coordinates
// first, before the previously accumulated CTM.
function composeMatrix(outer: Matrix, inner: Matrix): Matrix {
    return [
        outer[0] * inner[0] + outer[2] * inner[1],
        outer[1] * inner[0] + outer[3] * inner[1],
        outer[0] * inner[2] + outer[2] * inner[3],
        outer[1] * inner[2] + outer[3] * inner[3],
        outer[0] * inner[4] + outer[2] * inner[5] + outer[4],
        outer[1] * inner[4] + outer[3] * inner[5] + outer[5],
    ];
}

function applyMatrix(m: Matrix, x: number, y: number): [number, number] {
    return [x * m[0] + y * m[2] + m[4], x * m[1] + y * m[3] + m[5]];
}

export async function getImageRects(pdfDocument: any, pageNumber: number, OPS: any): Promise<ImageRectsResponse> {
    const response: ImageRectsResponse = { pageNumber, rects: [], failed: false };
    if (!Number.isInteger(pageNumber) || pageNumber < 1 || pageNumber > pdfDocument.numPages) {
        response.failed = true;
        return response;
    }
    try {
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: CSS_UNITS });
        const opList = await page.getOperatorList();
        const stack: Matrix[] = [];
        let ctm: Matrix = IDENTITY;
        for (let i = 0; i < opList.fnArray.length; i++) {
            const fn = opList.fnArray[i];
            const args = opList.argsArray[i];
            if (fn === OPS.save) {
                stack.push(ctm);
            } else if (fn === OPS.restore) {
                ctm = stack.pop() ?? IDENTITY;
            } else if (fn === OPS.transform) {
                ctm = composeMatrix(ctm, args as Matrix);
            } else if (fn === OPS.paintFormXObjectBegin) {
                // args: [matrix, bbox] — behaves like save + optional transform.
                stack.push(ctm);
                if (Array.isArray(args?.[0]) && args[0].length === 6) {
                    ctm = composeMatrix(ctm, args[0] as Matrix);
                }
            } else if (fn === OPS.paintFormXObjectEnd) {
                ctm = stack.pop() ?? IDENTITY;
            } else if (
                fn === OPS.paintImageXObject
                || fn === OPS.paintInlineImageXObject
                || fn === OPS.paintImageMaskXObject
                || (OPS.paintImageXObjectRepeat !== undefined && fn === OPS.paintImageXObjectRepeat)
            ) {
                // Unit square through the CTM → user space → viewport (markup) space.
                const corners: Array<[number, number]> = [[0, 0], [1, 0], [1, 1], [0, 1]];
                const xs: number[] = [];
                const ys: number[] = [];
                for (const [ux, uy] of corners) {
                    const [x, y] = applyMatrix(ctm, ux, uy);
                    const p = viewport.convertToViewportPoint(x, y);
                    xs.push(p[0]);
                    ys.push(p[1]);
                }
                const left = Math.min(...xs);
                const top = Math.min(...ys);
                const rect: MatchRect = { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
                if (rect.width > 1 && rect.height > 1) {
                    response.rects.push(rect);
                }
            }
        }
    } catch (err) {
        console.error(`getImageRects: page ${pageNumber} failed`, err);
        response.failed = true;
        response.rects = [];
    }
    return response;
}
